import { LIMIT_SIZE_1000 } from '@/main/app.constants';
import { setTemplateInstanceCount, setTemplateOutput, setTemplateVersionState } from '@/tools/models/template.actions';
import {
  fetchTemplate,
  getTemplateFeatureCanonicalIds,
  templateOutputFromResetDraft,
  toOutputItemMetadata,
} from '@/tools/models/template.utilities';
import { getDerivedFeatureId } from '@/tools/models/templateFormatting.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { API_TYPES_TO_ITEM_TYPES } from '@/trendData/trendData.constants';
import { getDependencies } from '@/utilities/formula.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { sqUsersApi } from '@seeqdev/api/endpoint/UsersApi';
import { sqTemplatesGqlApi } from '@seeqdev/api/graphql/sqTemplatesGqlApi';
import { SeeqNames } from '@seeqdev/constants';
import type {
  FormulaOutput,
  NewlyAddedDerivedFeature,
  OutputDependencyItem,
  OutputItemMetadataFields,
  TemplateType,
  TemplateVersionMetadata,
} from '@/tools/models/template.types';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';
import type { TemplateApplicationMappingV1 } from '@seeqdev/api/model/TemplateApplicationMappingV1';
import type { TemplateInputV1 } from '@seeqdev/api/model/TemplateInputV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

// TODO: CRAB-61377: Remove once template rename migration has ran
// Legacy datasource names from before the rename migration. Kept for backward compatibility
// while the async rename migration completes. Remove once the migration is confirmed done.
const LEGACY_DATASOURCE_CLASS = 'Seeq Models';
const LEGACY_USER_DATASOURCE_ID = 'User Models';

/** Creates a new template from TemplateInputV1. Returns the created template output with ID and versionId. */
export const createTemplate = async (input: TemplateInputV1) => {
  const { data } = await sqTemplatesApi.createTemplate(input);
  return data;
};

/** Edits the given template version. Callers compose this with `usePublishTemplateVersionMutation`
 *  when they need to publish the edits as the new production version. */
export const editTemplateVersion = async (templateId: string, versionId: string, input: TemplateInputV1) => {
  await sqTemplatesApi.editTemplateVersion(input, { templateId, versionId });
};

/** Saves changes to a draft without locking or publishing. */
export const saveDraft = async (templateId: string, versionId: string, input: TemplateInputV1) => {
  await sqTemplatesApi.editTemplateVersion(input, { templateId, versionId });
};

/** Fetches a template for editing, populating store state and marking inputs as collapsed.
 * Transparently creates a draft so edits are versioned. If a draft already exists it is
 * reused; if the template has no version history yet the initial unlocked template is loaded directly.
 * Also fetches and stores the instance count for the template.
 * Returns the fetched template so callers can read its just-fetched derivedFeatures/inputFeatures
 * directly instead of re-reading the store afterward.
 * Pass a table application's `mappings` to bind the template's features to their table columns (`columnDefinition`).
 */
export const fetchTemplateForEditing = async (
  id: string,
  templateType: TemplateType,
  mappings?: TemplateApplicationMappingV1[],
): Promise<TemplateOutputV1 | undefined> => {
  // Try to resume an existing draft first
  try {
    const { data: draft } = await sqTemplatesApi.getDraft({ templateId: id });
    const template = await fetchTemplate(draft.versionId!, templateType, true, true, mappings);
    await populateEditBaseCanonicalIds(id, draft.baseVersionId, templateType);
    await populateInstanceCount(id, templateType);
    return template;
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status !== 404) {
      throw error;
    }
  }

  // No draft exists — create one
  try {
    const { data: draft } = await sqTemplatesApi.createDraft({ templateId: id });
    const template = await fetchTemplate(draft.versionId!, templateType, true, true, mappings);
    await populateEditBaseCanonicalIds(id, draft.baseVersionId, templateType);
    await populateInstanceCount(id, templateType);
    return template;
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 400) {
      // No version history yet — edit the initial unlocked template directly
      const template = await fetchTemplate(id, templateType, true, true, mappings);
      setTemplateVersionState(templateType, {
        isLocked: false,
        isDraft: false,
        sourceVersionNumber: undefined,
        editBaseFeatureCanonicalIds: undefined,
      });
      await populateInstanceCount(id, templateType);
      return template;
    }
    throw error;
  }
};

/**
 * Overwrites the current user's in-progress draft with `sourceVersionId`'s content, discarding whatever
 * edits the draft previously held. Used when switching which version an Edit-pane draft is based on.
 *
 * The mutation's response already carries the draft's full post-reset content and the resolved source
 * version's number, so the store is updated directly from it rather than re-fetching over REST.
 */
export const switchEditDraftVersion = async (
  templateId: string,
  currentDraftVersionId: string,
  sourceVersionId: string,
  templateType: TemplateType,
): Promise<TemplateOutputV1 | undefined> => {
  const { resetTemplateDraft } = await sqTemplatesGqlApi.resetTemplateDraft({
    input: { templateContainerId: templateId, sourceVersionId },
  });

  const template = templateOutputFromResetDraft(templateId, currentDraftVersionId, resetTemplateDraft);
  setTemplateOutput(templateType, template);
  setTemplateVersionState(templateType, {
    isLocked: false,
    isDraft: true,
    sourceVersionNumber: resetTemplateDraft.sourceVersion?.number,
    editBaseFeatureCanonicalIds: getTemplateFeatureCanonicalIds(template),
  });

  await populateInstanceCount(templateId, templateType);
  return template;
};

/**
 * Fetches the version list and edit-base canonical IDs for a draft template.
 * Uses the baseVersionId from the draft response when available, falling back
 * to the production version for legacy drafts.
 */
async function populateEditBaseCanonicalIds(
  templateId: string,
  baseVersionId: string | undefined,
  templateType: TemplateType,
) {
  try {
    const { data } = await sqTemplatesApi.getTemplateVersions({ templateId });
    const productionVersion = data.versions.find((v) => v.production);

    // Use baseVersionId from draft response if available, otherwise fall back to production
    const editBaseVersionId = baseVersionId ?? data.productionVersionId ?? productionVersion?.versionId;

    const editBaseFeatureCanonicalIds = editBaseVersionId
      ? await sqTemplatesApi
          .getTemplateVersion({ templateId, versionId: editBaseVersionId })
          .then(({ data }) => getTemplateFeatureCanonicalIds(data))
          .catch(() => [])
      : [];

    // Find the version number for the edit base (the version the draft was created from)
    const editBaseVersion = editBaseVersionId
      ? data.versions.find((v) => v.versionId === editBaseVersionId)
      : undefined;

    setTemplateVersionState(templateType, {
      isLocked: false,
      isDraft: true,
      sourceVersionNumber: editBaseVersion?.version ?? productionVersion?.version,
      editBaseFeatureCanonicalIds,
    });
  } catch {
    // If version fetch fails, still mark as draft (unknown edit base)
    setTemplateVersionState(templateType, {
      isLocked: false,
      isDraft: true,
      sourceVersionNumber: undefined,
      editBaseFeatureCanonicalIds: undefined,
    });
  }
}

/**
 * Fetches version and draft metadata for a single template container.
 * Returns null if version data cannot be fetched (e.g. no Locking license).
 */
export const fetchTemplateVersionMetadata = async (templateId: string): Promise<TemplateVersionMetadata> => {
  const [versionsResult, draftResult] = await Promise.allSettled([
    sqTemplatesApi.getTemplateVersions({ templateId }),
    sqTemplatesApi.getDraft({ templateId }),
  ]);

  const versions = versionsResult.status === 'fulfilled' ? versionsResult.value.data.versions : [];

  const hasDraft = draftResult.status === 'fulfilled';
  const draftVersionId = hasDraft ? draftResult.value.data.versionId : undefined;

  const productionVersion = versions.find((v) => v.production);

  return {
    hasDraft,
    draftVersionId,
    productionVersionNumber: productionVersion?.version,
    versionCount: versions.length,
    isLocked: versions.length > 0,
    versions,
  };
};

/**
 * Fetches effective permissions for multiple template containers in parallel.
 * Returns a map of templateId -> permissions.
 */
export const fetchTemplatePermissions = async (
  templateIds: string[],
): Promise<Map<string, PermissionsV1 | undefined>> => {
  const results = await Promise.allSettled(
    templateIds.map((id) => sqItemsApi.getItemAndAllProperties({ id }).then((res) => ({ id, data: res.data }))),
  );

  const permissionsMap = new Map<string, PermissionsV1 | undefined>();
  for (const result of results) {
    if (result.status === 'fulfilled') {
      permissionsMap.set(result.value.id, result.value.data.effectivePermissions);
    }
  }
  return permissionsMap;
};

/**
 * Fetches version and draft metadata for multiple template containers in parallel.
 * Returns a map of templateId -> TemplateVersionMetadata.
 */
export const fetchAllTemplateVersionMetadata = async (
  templateIds: string[],
): Promise<Map<string, TemplateVersionMetadata>> => {
  const results = await Promise.allSettled(
    templateIds.map((id) => fetchTemplateVersionMetadata(id).then((metadata) => ({ id, metadata }))),
  );

  const metadataMap = new Map<string, TemplateVersionMetadata>();
  for (const result of results) {
    if (result.status === 'fulfilled') {
      metadataMap.set(result.value.id, result.value.metadata);
    }
  }
  return metadataMap;
};

/** Fetches template container items scoped to the given workbook IDs. */
export async function fetchTemplateItems(workbookIds: string[]) {
  // TODO: CRAB-60044 Replace search items usage with templates search endpoint
  const types = [SeeqNames.Types.ModelContainer];
  const makeFilters = (datasourceClass: string, datasourceId: string) => [
    `${SeeqNames.Properties.DatasourceClass}==${datasourceClass} && ${SeeqNames.Properties.DatasourceId}==${datasourceId}`,
  ];
  // TODO: CRAB-61377: Remove extra call for fetching templates once template rename migration has ran
  const [{ data }, { data: legacyData }] = await Promise.all([
    sqItemsApi.searchItems({
      types,
      filters: makeFilters(
        SeeqNames.LocalDatasources.Templates.DatasourceClass,
        SeeqNames.LocalDatasources.Templates.User.DatasourceId,
      ),
      scope: workbookIds,
      limit: LIMIT_SIZE_1000,
    }),
    sqItemsApi.searchItems({
      types,
      filters: makeFilters(LEGACY_DATASOURCE_CLASS, LEGACY_USER_DATASOURCE_ID),
      scope: workbookIds,
      limit: LIMIT_SIZE_1000,
    }),
  ]);

  const seenIds = new Set<string>();
  const items: ItemSearchPreviewV1[] = [];
  for (const item of [...(data.items ?? []), ...(legacyData.items ?? [])]) {
    if (item.id && !seenIds.has(item.id)) {
      seenIds.add(item.id);
      items.push(item);
    }
  }
  return items;
}

/** Fetches template container items shared with the current user, filtered by datasource ID. */
export async function fetchSharedTemplateItems(
  datasourceId: string,
  workbookId: string,
  includeGlobal: boolean,
): Promise<ItemSearchPreviewV1[]> {
  // TODO: CRAB-61377: Remove extra call for getItemsSharedWithMe once template rename migration has ran
  const legacyDatasourceId = datasourceId.replace(' Templates', ' Models');

  const [{ data }, { data: legacyData }] = await Promise.all([
    sqUsersApi.getItemsSharedWithMe({
      types: [SeeqNames.Types.ModelContainer],
      datasourceClass: SeeqNames.LocalDatasources.Templates.DatasourceClass,
      datasourceId,
      limit: LIMIT_SIZE_1000,
      scopedTo: workbookId,
      includeGlobal,
    }),
    sqUsersApi.getItemsSharedWithMe({
      types: [SeeqNames.Types.ModelContainer],
      datasourceClass: LEGACY_DATASOURCE_CLASS,
      datasourceId: legacyDatasourceId,
      limit: LIMIT_SIZE_1000,
      scopedTo: workbookId,
      includeGlobal,
    }),
  ]);

  // Merge and deduplicate. Normalize datasource field to use new names so downstream
  // code that checks datasource.id gets a consistent view.
  const seenIds = new Set<string>();
  const items: ItemSearchPreviewV1[] = [];
  for (const item of [...(data.items ?? []), ...(legacyData.items ?? [])]) {
    if (item.id && !seenIds.has(item.id)) {
      seenIds.add(item.id);
      items.push({
        ...item,
        datasource: {
          id: datasourceId,
          name: datasourceId,
          type: SeeqNames.Types.Datasource,
        },
      });
    }
  }
  return items;
}

/** Fetches the saved formula for each derived feature that has an id. Errors are swallowed
 * (with a toast) so a single failure does not abort the rest of the batch. Returns a map keyed
 * by featureId for callers that need to look up formulas during template formatting. */
export const fetchFormulasForFeatures = async (
  derivedFeatures: NewlyAddedDerivedFeature[],
): Promise<Map<string, FormulaOutput>> => {
  const formulasByFeatureId = new Map<string, FormulaOutput>();
  await Promise.all(
    derivedFeatures.map(async (feature) => {
      const featureId = getDerivedFeatureId(feature);
      if (!featureId) return;

      // Skip if feature already has a formulaCreator rule
      const existingFormulaCreator = feature?.rules?.[0]?.formulaCreator;
      if (existingFormulaCreator) return;

      try {
        const response = await sqFormulasApi.getItem({ id: featureId });
        if (response?.data) formulasByFeatureId.set(featureId, response.data);
      } catch (error) {
        errorToast({ httpResponseOrError: error });
      }
    }),
  );
  return formulasByFeatureId;
};

/** Fetches the dependencies of the given parameters and returns a deduped list of dependencies. */
export const fetchDependenciesOfParameters = async (
  parameterItemIds: string[],
): Promise<{
  dependencyList: OutputDependencyItem[];
  dependenciesByParameterId: Map<string, OutputDependencyItem[]>;
}> => {
  const dependenciesByParameterId = new Map<string, OutputDependencyItem[]>();
  const allDependenciesById = new Map<string, OutputDependencyItem>();

  // Dedupe parameter IDs to avoid redundant API calls
  const uniqueParameterIds = [...new Set(parameterItemIds)];

  await Promise.all(
    uniqueParameterIds.map(async (id) => {
      const dependencies = (await getDependencies({ id }))?.dependencies ?? [];
      dependenciesByParameterId.set(id, dependencies);
      dependencies.forEach((dep: OutputDependencyItem) => allDependenciesById.set(dep.id, dep));
    }),
  );

  return { dependencyList: [...allDependenciesById.values()], dependenciesByParameterId };
};

/**
 * Fetches metadata for a list of item IDs. Used to collect output type and unit of measure
 * information for intermediate features when formatting dependencies.
 *
 * @param itemIds - Array of item IDs to fetch metadata for
 * @returns Map of item ID to metadata fields (outputItemType, outputItemMetadata)
 */
export const fetchItemsForMetadata = async (itemIds: string[]): Promise<Map<string, OutputItemMetadataFields>> => {
  if (itemIds.length === 0) return new Map();

  const results = await Promise.allSettled(
    itemIds.map(async (id) => {
      const { data } = await sqItemsApi.getItemAndAllProperties({ id });
      const itemType = API_TYPES_TO_ITEM_TYPES[data.type] || data.type;
      // Signals/Conditions use ValueUom, Scalars use Uom
      const valueUnitOfMeasure =
        data.properties?.find((prop) => prop.name === SeeqNames.Properties.ValueUom)?.value ??
        data.properties?.find((prop) => prop.name === SeeqNames.Properties.Uom)?.value;
      return {
        ...data,
        itemType,
        valueUnitOfMeasure,
      };
    }),
  );

  const itemResponses = results.flatMap((result) => {
    if (result.status === 'fulfilled') return [result.value];
    errorToast({ httpResponseOrError: result.reason });
    return [];
  });

  const decoratedItems = decorate({ items: itemResponses });
  const itemsArray = Array.isArray(decoratedItems) ? decoratedItems : [decoratedItems];

  const metadataMap = new Map<string, OutputItemMetadataFields>();
  itemsArray.forEach((item) => {
    if (item.id) {
      metadataMap.set(item.id, toOutputItemMetadata(item));
    }
  });

  return metadataMap;
};

/**
 * Fetches the count of distinct template applications for a template and sets it on the store.
 */
async function populateInstanceCount(templateId: string, templateType: TemplateType) {
  try {
    const { data } = await sqTemplatesApi.getApplicationCountForTemplate({ id: templateId });
    setTemplateInstanceCount(templateType, data.count);
  } catch {
    setTemplateInstanceCount(templateType, undefined);
  }
}

/**
 * Fetches the list of scaling tables to which a template has been applied.
 * Returns an array of table IDs that can be used to filter the search widget.
 * @throws Will throw an error if the API call fails.
 */
export const fetchAppliedTemplateTables = async (templateId: string): Promise<string[]> => {
  const { data } = await sqTemplatesApi.getAppliedTemplateTables({ templateId });
  return data.map((table) => table.id).filter((id): id is string => id !== undefined);
};
